import { getIdFromImageAsset, loadAssets, ResourceManager } from '@goodboydigital/astro';
import { AtlasAttachmentLoader, SkeletonJson } from '@pixi-spine/runtime-3.8';
import { dirname, extname } from 'path';
import { TextureAtlas } from 'pixi-spine';
import { BaseTexture } from 'pixi.js';

/**
 * loader plugin that parses sprite sheets!
 * once the json has been loaded this checks to see if the json is spritesheet data.
 * if it is, we load the spritesheets image and parse the data into PIXI.Spritesheet
 * All textures in the sprite sheet are then added to the cache
 */
export const loadSpine3xAtlas = {

    test(url: string): boolean
    {
        const extension = extname(url);

        return extension.includes('.atlas') && extension.indexOf('~') < 0; // Ignore files with that included in extension
    },

    async load(url: string): Promise<any>
    {
        const response = await fetch(url);
        const rawAtlas = await response.text();

        return new Promise((resolve) =>
        {
            // eslint-disable-next-line no-undef
            const textureAtlas = new TextureAtlas(rawAtlas, textureLoader(dirname(url)), () =>
            {
                resolve(textureAtlas);
            });
        });
    },
};

function textureLoader(baseUrl: string)
{
    if (baseUrl && baseUrl.lastIndexOf('/') !== (baseUrl.length - 1))
    {
        baseUrl += '/';
    }

    return (line: string, callback: (baseTexture: BaseTexture)=>void) =>
    {
        const url = baseUrl + line;

        loadAssets(url).then((asset) =>
        {
            callback(asset.baseTexture);
        });
    };
}

/**
 * loader plugin that parses spine animations!
 */
export const loadSpine3x = {

    testParse(asset: {bones: any}, url: string): boolean
    {
        return (extname(url).includes('.json') && !!asset.bones);
    },

    async parse(spineData: Record<string, unknown>, url: string): Promise<any>
    {
        const atlasPath = getAtlasPath(url);
        const textureAtlas = await loadAssets(atlasPath);

        if (textureAtlas)
        {
            const attachmentLoader = new AtlasAttachmentLoader(textureAtlas);

            const spineParser = new SkeletonJson(attachmentLoader);

            const skeletonData = spineParser.readSkeletonData(spineData);

            return skeletonData;
        }

        return null;
    },
};

/**
 * HACK - Look into ResourceManager manifest for proper atlas path
 * will fallback to an atlas with the same filename as the provided json file
 * @param jsonPath -
 */
function getAtlasPath(jsonPath: string): string
{
    let atlasPath = jsonPath.replace('.json', '.atlas');
    const version = ResourceManager['_version'];
    const manifest = ResourceManager['_manifest'];
    const formats = ResourceManager['_imageFormatPreference'];
    const basePath = ResourceManager['_basePath'];

    if (basePath !== '')
    {
        atlasPath = atlasPath.split(basePath)[1];
        jsonPath = jsonPath.split(basePath)[1];
    }

    for (const groupName in manifest)
    {
        const group = manifest[groupName];

        if (group.json[jsonPath])
        {
            const atlasVersion = getIdFromImageAsset(group.atlas, atlasPath, version, formats);

            if (group.atlas[atlasPath] && atlasVersion)
            {
                return basePath + atlasVersion as string;
            }
        }
    }

    return basePath + atlasPath;
}
